import Cookies from "js-cookie";
import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { TailSpin } from "react-loader-spinner";
import { FiEye } from "react-icons/fi";
import { getApi } from "../../utils/api";
import { API_REQUEST_ACCESS_LIST } from "../../config/Endpoints";
import { capitalize, formatDate } from "../../utils/helpers";
import GenerateAccessProgress from "./GenerateAccessProgress";
import {
  Badge,
  ResponsiveDataTable,
} from "../../v2/components/ui/index.ts";
import { EmptyStateMessage } from "../../v2/components/shared/empty-state-message/empty-state-message.tsx";

import styles from "./it-requests-list.module.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40];

const AccessRequestList = ({ title, description, onTotalRequestChange }) => {
  const [accessList, setAccessList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [openProgressIndexes, setOpenProgressIndexes] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState();
  const [accessProgressData, setAccessProgressData] = useState({});

  const accessToken = Cookies.get("accessToken");
  const userData = localStorage.getItem("userData");
  const employee = userData ? JSON.parse(userData) : null;
  const employeeId = employee?.id;
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRequest, setTotalRequest] = useState(0);

  const handleOpenProgress = (index, item) => {
    if (!openProgressIndexes.includes(index)) {
      setAccessProgressData(item);
      setIsProgressOpen(true);
      setSelectedIndex(index);

      setOpenProgressIndexes([...openProgressIndexes, index]);
    }
  };

  const handleCloseProgress = (index) => {
    setAccessProgressData({});
    setIsProgressOpen(false);
    setSelectedIndex();
    setOpenProgressIndexes(openProgressIndexes.filter((i) => i !== index));
  };

  const tableData = useMemo(
    () =>
      Array.isArray(filteredItems)
        ? filteredItems.map((item, index) => ({
          accessType: item?.accessType?.name,
          type: item?.type || "-",
          projectName: item.projectName,
          dlName: item.dlName || "-",
          reason: item?.reason,
          requestedStartDate: item?.createdAt
            ? formatDate(item.createdAt)
            : "-",
          requestedEndDate: item?.requestedEndDate
            ? formatDate(item.requestedEndDate)
            : "Ongoing",
          status: item?.secondApproveStatus,
          _original: item,
          _index: index,
        }))
        : [],
    [filteredItems]
  );

  const columns = useMemo(
    () => [
      {
        id: "accessType",
        header: "Access Type",
        accessor: "accessType",
        sortable: true,
        getSortValue: (row) => String(row.accessType ?? ""),
      },
      {
        id: "type",
        header: "Role",
        accessor: "type",
        sortable: true,
        getSortValue: (row) => String(row.type ?? ""),
      },
      {
        id: "projectName",
        header: "Project Name",
        sortable: true,
        getSortValue: (row) => String(row.projectName ?? ""),
        render: (row) => (row.projectName ? row.projectName : "All Projects"),
      },
      {
        id: "dlName",
        header: "Delivery Lead",
        accessor: "dlName",
        sortable: true,
        getSortValue: (row) => String(row.dlName ?? ""),
      },
      {
        id: "reason",
        header: "Reason",
        accessor: "reason",
        sortable: true,
        getSortValue: (row) => String(row.reason ?? ""),
        truncate: true,
        getTitleText: (row) => String(row.reason ?? ""),
      },
      {
        id: "requestedStartDate",
        header: "Requested Start Date",
        accessor: "requestedStartDate",
        sortable: true,
        getSortValue: (row) => new Date(row._original.requestDate).getTime(),
      },
      {
        id: "requestedEndDate",
        header: "Requested End Date",
        accessor: "requestedEndDate",
        sortable: true,
        getSortValue: (row) => new Date(row._original.requestDate).getTime(),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        getSortValue: (row) => {
          const first = row._original?.firstApproveStatus;
          const second = row._original?.secondApproveStatus;

          if (first === "REJECTED") return "rejected";
          if (second) return second.toLowerCase();
          return "";
        },
        render: (row) => {
          const first = row._original.firstApproveStatus;
          const second = row._original.secondApproveStatus;

          // Determine final status label
          const finalStatus =
            first === "REJECTED"
              ? "Rejected"
              : second
                ? capitalize(second)
                : "";

          // Determine badge color
          const variant =
            first === "REJECTED"
              ? "danger"
              : second === "PENDING"
                ? "warning"
                : second === "REJECTED"
                  ? "danger"
                  : second === "DEALLOCATED"
                    ? "neutral"
                    : second === "APPROVED"
                      ? "success"
                      : "neutral";

          return (
            <Badge variant={variant} >
              {finalStatus}
            </Badge>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => {
          const index = row._index;
          const isOpen = openProgressIndexes.includes(index);
          return (
            <button
              type="button"
              className="action-btn action-btn--view"
              onClick={() => handleOpenProgress(index, row._original)}
            >
              <FiEye size={14} />
              View
            </button>
          );
        },
      },
    ],
    [openProgressIndexes]
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (nextLimit) => {
    setLimit(nextLimit);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchList();
  }, [currentPage, limit, employeeId]);

  const fetchList = () => {
    if (!employeeId) return;
    setIsLoading(true);
    getApi(
      `${API_REQUEST_ACCESS_LIST}/${employeeId}?pageSize=${limit}&pageNo=${currentPage}&status=`,
      accessToken
    )
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          const content = res.data.data?.content || [];
          setAccessList(content);
          setFilteredItems(content);
          setTotalPages(res.data.data?.totalPages || 1);
          setTotalRequest(res.data.data?.totalElements);
          if (typeof onTotalRequestChange === "function") {
            onTotalRequestChange(res.data.data?.totalElements || 0);
          }
        }
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
        setFilteredItems([]);
      });
  };

  useEffect(() => {
    if (isProgressOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isProgressOpen]);

  return (
    <>
      <div className={styles.listRoot}>
        {isLoading ? (
          <div className={styles.loadingRow}>
            <TailSpin
              height="60"
              width="60"
              color="var(--color-accent-600, #2563eb)"
              ariaLabel="loading-access-requests"
              visible
            />
          </div>
        ) : (
          <ResponsiveDataTable
            columns={columns}
            data={tableData}
            aria-label="Access Requests"
            pagination
            page={currentPage}
            pageSize={limit}
            totalItems={totalRequest ?? 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handleLimitChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalLabel={
              <span className={styles.totalLabel}>
                Total Access Requests: {totalRequest ?? 0}
              </span>
            }
            emptyState={
              <EmptyStateMessage message="No Records Available." />
            }
          />
        )}
      </div>

      {isProgressOpen && (
        <>
          <div
            className="sidebar-backdrop"
            onClick={() => handleCloseProgress(selectedIndex)}
          ></div>

          <aside className="right-sidebar asset-request-progress-sidebar">
            <div
              className="mb-4 page-heading"
              onClick={() => handleCloseProgress(selectedIndex)}
            >
              <h1>{accessProgressData?.accessType?.name}</h1>
              <span className="close_icon">&#10005;</span>
            </div>

            <div className="detail">
              <h5>
                Status:{" "}
                {accessProgressData?.secondApproveStatus === "PENDING"
                  ? "Pending"
                  : accessProgressData?.secondApproveStatus === "REJECTED"
                    ? "Rejected"
                    : accessProgressData?.secondApproveStatus === "APPROVED"
                      ? "Approved"
                      : "-"}
              </h5>
            </div>

            <ul className="track-progress ">
              <li className="approved mb-2">
                <h6>Requested</h6>
              </li>
              {GenerateAccessProgress(accessProgressData)}
            </ul>
          </aside>
        </>
      )}
    </>
  );
};

export default AccessRequestList;
